// This is the base for splide carousel, the full documentation can be found on https://splidejs.com/events/
// The Grid extension is just used when the carousel display are not in line but on a group,
// So the carousel changes the whole group of elements and not one by one.
// The grid extensions and functions can be deleted if it's not in the website design.

import Splide from '@splidejs/splide';
import { merge } from 'lodash';
// import Grid from '@splidejs/splide-extension-grid';

const bootstrapBreakpoints = {
	xs: 375,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1400,
};

const splideArrowPath =
	'M16.6367 14.8591L22.4991 20.6075L16.6367 26.3559L18.8574 28.5334L26.9404 20.6075L18.8574 12.6816L16.6367 14.8591Z';

const splideConfiguration = {
	default: {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 3,
		pagination: true,
		focus: 1,
		mediaQuery: 'min',
		breakpoints: {
			640: {
				perPage: 3,
			},
		},
	},
	'card-deck': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		pagination: true,
		focus: 'center',
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.sm ]: {
				perPage: 1,
				padding: '0.5em'
			},
			[ bootstrapBreakpoints.lg ]: {
				perPage: 3,
			},
		},
	},
	'text-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		focus: 0,
		pagination: true,
		arrows: true,
		breakpoints: {
			640: {
				perPage: 1,
				focus: 0,
			},
		},
	},

	'announcement-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		perMove: 1,
		focus: 0,
		pagination: true,
		arrows: true,
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.md ]: {
				pagination: false,
			},
		},
	},

	'image-carousel': {
		type: 'loop',
		start: 0,
		perPage: 1.5,
		perMove: 1,
		focus: 'center',
		pagination: false,
		arrows: false,
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.md ]: {
				perPage: 3,
			},
			[ bootstrapBreakpoints.lg ]: {
				perPage: 5,
			}
		},
	},
	'featured-project-carousel': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		focus: 'center',
		pagination: true,
		arrows: true,
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.md ]: {
				arrows: false,
				perPage: 2,
			},
			[ bootstrapBreakpoints.lg ]: {
				perPage: 3,
			},
			[ bootstrapBreakpoints.xl ]: {
				destroy: true,
			},
		},
	},

	'location-featured-project': {
		arrowPath: splideArrowPath,
		type: 'loop',
		start: 0,
		perPage: 1,
		focus: 'center',
		pagination: true,
		arrows: true,
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.md ]: {
				arrows: false,
				perPage: 3,
			},
			[ bootstrapBreakpoints.lg ]: {
				pagination: false,
				arrows: false,
				drag: false,
				perPage: 6
			},
		},
	},

	'single-project-carousel': {
		type      : 'fade',
		rewind    : true,
		pagination: false,
		arrows    : false,
	},
	'single-project-carousel-thumbnail': {
		fixedWidth  : 80,
		fixedHeight : 60,
		gap         : 5,
		rewind      : true,
		pagination  : false,
		isNavigation: true,
		mediaQuery: 'min',
		breakpoints: {
			[ bootstrapBreakpoints.md ]: {
				fixedWidth : 100,
				fixedHeight: 80,
				gap        : 10,
			},
		}
	}
};

function initializeSplide() {
	const splides = document.getElementsByClassName( 'splide' );
	const spliders = {};
	[ ...splides ].forEach( ( splide ) => {
		// Skip if parent block has no-carousel or is-style-paginated class
		if (splide.closest('.block-featured-project.no-carousel')) return;
		if (splide.closest('.block-featured-project.is-style-paginated')) return;

		let config = splideConfiguration.default;

		const configAttr = splide.getAttribute( 'data-splide-config' );
		if ( configAttr !== undefined && configAttr !== '' ) {
			config = splideConfiguration[ configAttr ];
		}

		// Check for disable settings
		const disableAttr = splide.getAttribute( 'data-splide-disable' );
		if ( disableAttr !== null && disableAttr !== '' ) {
			const disableBreakpoints = disableAttr.split( ',' );

			// Track if a breakpoint has been disabled.
			let disableFlag = false;

			for ( const breakpoint in bootstrapBreakpoints ) {
				if ( disableBreakpoints.includes( breakpoint ) ) {
					const disableObj = {};
					disableObj[ bootstrapBreakpoints[ breakpoint ] ] = {
						destroy: true,
					};

					config = merge( config, {
						breakpoints: disableObj,
					} );

					disableFlag = true;
				} else if ( disableFlag === true ) {
					const disableObj = {};
					disableObj[ bootstrapBreakpoints[ breakpoint ] ] = {
						destroy: false,
					};

					config = merge( config, {
						breakpoints: disableObj,
					} );

					disableFlag = false;
				}
			}
		}

		const splider = new Splide( splide, config ).mount()
		spliders[splide.getAttribute( 'data-splide-config' )] = splider;

		const mainSplider = splide.getAttribute( 'data-target-parent-thumbnail' );

		if(mainSplider) spliders[mainSplider].sync(splider);
	} );
}

function initFeaturedProjectLoadMore() {
	const containers = document.querySelectorAll('.block-featured-project');

	containers.forEach(container => {
		if (container.classList.contains('is-style-paginated')) return;

		const splideList = container.querySelector('.splide__list');

		if (!splideList) return;

		// Get or create button (fallback for caching issues)
		let loadMoreBtn = container.querySelector('.featured-project__load-more');
		if (!loadMoreBtn) {
			const btnWrapper = document.createElement('div');
			btnWrapper.className = 'wp-block-button has-btn-secondary btn-load-more';
			btnWrapper.innerHTML = '<button class="wp-block-button__link wp-element-button featured-project__load-more">Load More</button>';
			container.appendChild(btnWrapper);
			loadMoreBtn = btnWrapper.querySelector('.featured-project__load-more');
		}

		const slides = [...splideList.querySelectorAll('.splide__slide')];
		let currentVisible = 6;

		const isNoCarousel = container.classList.contains('no-carousel');

		const renderSlides = () => {
			const splide = container.querySelector('.splide');
			const isCarouselActive = splide && splide.classList.contains('is-active');
			const btnContainer = loadMoreBtn.closest('.btn-load-more');

			// Show load more behavior if: no-carousel class OR carousel is destroyed
			if (isNoCarousel || !isCarouselActive) {
				// Hide button if 6 or fewer slides
				if (slides.length <= 6) {
					if (btnContainer) btnContainer.style.display = 'none';
					return;
				}

				// Show button container
				if (btnContainer) btnContainer.style.display = '';

				slides.forEach((slide, index) => {
					slide.style.display = index >= currentVisible ? 'none' : '';
				});

				if (currentVisible >= slides.length) {
					loadMoreBtn.textContent = 'All Projects Shown';
					loadMoreBtn.classList.add('inactive');
				}
			} else {
				// Carousel active - hide button and show all slides
				if (btnContainer) btnContainer.style.display = 'none';
				slides.forEach(slide => slide.style.display = '');
			}
		};

		renderSlides();
		window.addEventListener('resize', renderSlides);

		loadMoreBtn.addEventListener('click', () => {
			if (loadMoreBtn.classList.contains('inactive')) return;
			currentVisible += 6;
			renderSlides();
		});
	});
}

function initFeaturedProjectPagination() {
	const containers = document.querySelectorAll('.block-featured-project.is-style-paginated');

	containers.forEach(container => {
		const splideList = container.querySelector('.splide__list');
		if (!splideList) return;

		const perPage = parseInt(container.dataset.perPage, 10) || 6;
		const slides = [...splideList.querySelectorAll('.splide__slide')];

		if (slides.length <= perPage) return;

		let currentPage = 1;
		const totalPages = Math.ceil(slides.length / perPage);

		const renderPage = () => {
			const start = (currentPage - 1) * perPage;
			const end = start + perPage;
			slides.forEach((slide, i) => slide.classList.toggle('is-hidden', i < start || i >= end));
			prevBtn.disabled = currentPage === 1;
			nextBtn.disabled = currentPage === totalPages;
			pageInfo.textContent = `${currentPage} / ${totalPages}`;
		};

		const pagination = document.createElement('div');
		pagination.className = 'featured-project__pagination';

		const prevWrapper = document.createElement('div');
		prevWrapper.className = 'wp-block-button has-btn-secondary';
		const prevBtn = document.createElement('button');
		prevBtn.className = 'wp-block-button__link wp-element-button';
		prevBtn.textContent = 'Previous';
		prevWrapper.appendChild(prevBtn);

		const pageInfo = document.createElement('span');
		pageInfo.className = 'featured-project__pagination-info';

		const nextWrapper = document.createElement('div');
		nextWrapper.className = 'wp-block-button has-btn-secondary';
		const nextBtn = document.createElement('button');
		nextBtn.className = 'wp-block-button__link wp-element-button';
		nextBtn.textContent = 'Next';
		nextWrapper.appendChild(nextBtn);

		pagination.appendChild(prevWrapper);
		pagination.appendChild(pageInfo);
		pagination.appendChild(nextWrapper);
		container.appendChild(pagination);

		prevBtn.addEventListener('click', () => {
			if (currentPage > 1) {
				currentPage--;
				renderPage();
				container.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		});

		nextBtn.addEventListener('click', () => {
			if (currentPage < totalPages) {
				currentPage++;
				renderPage();
				container.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		});

		renderPage();
	});
}

document.addEventListener( 'DOMContentLoaded', () => {
	initializeSplide();
	initFeaturedProjectLoadMore();
	initFeaturedProjectPagination();
} );
